import { z } from "zod";
export const feeStatsQuerySchema = z.object({
    blocks: z
        .string()
        .optional()
        .describe("Size of the trailing block window (default 100). Ignored when from/to are set.")
        .meta({ example: "200" }),
    from: z.string().optional().describe("Start block number (inclusive).").meta({ example: "4600" }),
    to: z.string().optional().describe("End block number (inclusive).").meta({ example: "5100" }),
});
export const clusterRecommendQuerySchema = z.object({
    vector: z
        .string()
        .describe("JSON array of 16 feature values describing the planned transaction shape, in this " +
        "exact order: [numNoteHashes, numNullifiers, numL2ToL1Msgs, numPrivateLogs, " +
        "numContractClassLogs, numPublicLogs, gasLimitDa, gasLimitL2, maxFeePerDaGas, " +
        "maxFeePerL2Gas, numSetupCalls, numAppCalls, hasTeardown (0|1), " +
        "totalPublicCalldataSize, expirationDelta (seconds), feePayer (address string)]. " +
        "The first 15 are numbers; feePayer is the categorical dimension.")
        .meta({ example: '[2,2,0,3,0,0,916,669400,0,3251365843721,0,1,0,4,3600,"0x24327cb55a2eeb1ad9b6d20b0b4f2ba940dbfa38f101ac0e93af7ee5a1a6ba7e"]' }),
    limit: z
        .string()
        .optional()
        .describe("Max recommendations to return (default 5, cap 20).")
        .meta({ example: "3" }),
    runId: z.string().optional().describe("Cluster run to use (default: latest run)."),
});
// ── Response schemas ──
// These are attached to the routes, so they both document the external API and
// act as the serialization contract. Handlers return one of several shapes
// (data, empty-window, error) — modelled as optional fields on one object.
export const statBlockSchema = z
    .object({
    min: z.string().nullable(),
    max: z.string().nullable(),
    mean: z.string().nullable(),
    median: z.string().nullable(),
    p75: z.string().nullable(),
})
    .describe("Distribution summary. Values are plain integer decimal strings, BigInt-safe (wei-scale " +
    "values exceed JS number range); median/p75 are observed sample values (percentile_disc), " +
    "mean is rounded to an integer. Null when no transaction in the window has the field.");
export const feeStatsResponseSchema = z.object({
    data: z.null().optional().describe("Null marker when the window contains no transactions."),
    blockRange: z
        .object({
        from: z.number().nullish().describe("First block of the analyzed window."),
        to: z.number().nullish().describe("Last block of the analyzed window."),
    })
        .optional(),
    txCount: z.number().optional().describe("Transactions in the window."),
    actualFee: statBlockSchema.optional(),
    gasLimitDa: statBlockSchema.optional(),
    gasLimitL2: statBlockSchema.optional(),
    maxFeePerDaGas: statBlockSchema.optional(),
    maxFeePerL2Gas: statBlockSchema.optional(),
    baseFee: z
        .object({
        da: z.string().nullable(),
        l2: z.string().nullable(),
    })
        .nullable()
        .optional()
        .describe("Base fee per gas of the latest block."),
}).meta({
    example: {
        blockRange: { from: 4649, to: 5100 },
        txCount: 173,
        actualFee: {
            min: "892996461559834900",
            max: "5256589538419134140",
            mean: "1284724861995943169",
            median: "1210424463968038100",
            p75: "1211755638491678200",
        },
        gasLimitDa: { min: "212", max: "117668", mean: "15703", median: "916", p75: "2922" },
        gasLimitL2: { min: "521951", max: "6540000", mean: "1445458", median: "669400", p75: "1048445" },
        maxFeePerDaGas: { min: "0", max: "0", mean: "0", median: "0", p75: "0" },
        maxFeePerL2Gas: {
            min: "1949475489097",
            max: "3985416615200",
            mean: "3258596272485",
            median: "3251365843721",
            p75: "3251365843721",
        },
        baseFee: { da: "0", l2: "3251365843721" },
    },
});
export const clusterRecommendationSchema = z.object({
    clusterId: z.number(),
    clusterSize: z.number().describe("Number of transactions in the cluster (anonymity-set size)."),
    distance: z.number().describe("Gower distance between the input vector and the cluster centroid (0 = identical)."),
    score: z.number().describe("clusterSize / (1 + distance) — higher is a better privacy set."),
    centroid: z.array(z.union([z.number(), z.string()])),
    deltas: z
        .record(z.string(), z.object({
        current: z.union([z.number(), z.string()]),
        target: z.union([z.number(), z.string()]),
    }))
        .describe("Per-feature differences between the input vector and the centroid."),
});
export const clusterRecommendResponseSchema = z.object({
    runId: z.number().describe("Cluster run the recommendations were computed against."),
    recommendations: z.array(clusterRecommendationSchema),
}).meta({
    example: {
        runId: 26859,
        recommendations: [
            {
                clusterId: 9,
                clusterSize: 646,
                distance: 0.258,
                score: 513.52,
                centroid: [0, 1, 0, 0, 0, 0, 141, 4240135, 0, 3498795352868.5, 0, 1, 0, 1401, 82800,
                    "0x0b2e0328359387a190a9c3a8db41e4962fa647249a0294101ef3c3fcc8576edb"],
                deltas: {
                    numNoteHashes: { current: 2, target: 0 },
                    gasLimitL2: { current: 669400, target: 4240135 },
                    totalPublicCalldataSize: { current: 4, target: 1401 },
                },
            },
        ],
    },
});
// ── Mana per tx ──
export const manaStatsQuerySchema = z.object({
    blocks: z
        .string()
        .optional()
        .describe("Size of the trailing block window (default 100). Ignored when from/to are set.")
        .meta({ example: "1000" }),
    from: z.string().optional().describe("Start block number (inclusive).").meta({ example: "4600" }),
    to: z.string().optional().describe("End block number (inclusive).").meta({ example: "5100" }),
});
export const manaDistributionSchema = z
    .object({
    min: z.number(),
    p10: z.number(),
    mean: z.number(),
    p50: z.number(),
    p95: z.number(),
    max: z.number(),
})
    .describe("Distribution of mana consumed per transaction, derived as actualFee ÷ the block's base " +
    "fee per mana (feePerL2Gas). Exact while the DA base fee is zero and no priority fee is " +
    "bid; otherwise a slight overestimate. Values are rounded to whole mana.");
export const manaStatsResponseSchema = z.object({
    data: z.null().optional().describe("Null marker when the window contains no transactions."),
    blockRange: z
        .object({
        from: z.number().nullish(),
        to: z.number().nullish(),
    })
        .optional(),
    txCount: z.number().optional().describe("Transactions with a fee in the window."),
    manaPerTx: manaDistributionSchema.optional(),
}).meta({
    example: {
        blockRange: { from: 4674, to: 5100 },
        txCount: 173,
        manaPerTx: { min: 490500, p10: 584386, mean: 1193013, p50: 946716, p95: 2449005, max: 3672622 },
    },
});
//# sourceMappingURL=service-api.js.map